import { SystemStyleObject, Text } from "@chakra-ui/react";
import { Select, SingleValue } from "chakra-react-select";
import React, { memo, useCallback, useMemo } from "react";
import colors from "app/colors";
import useQueryString from "modules/hooks/use-query-string";
import useInternalAdminTagCategoryList from "../hooks/use-internal-admin-tag-category-list";
import { InternalAdminTagCategoryFragment } from "gql/graphql";

interface IProps {
  onChange: (
    tagCategory: SingleValue<InternalAdminTagCategoryFragment>,
  ) => void;
  excludeTagCategoryIds?: string[];
}

const InternalAdminTagCategorySelect = ({
  onChange,
  excludeTagCategoryIds,
}: IProps) => {
  const { qsOrganizationId } = useQueryString();

  const { tagCategories } = useInternalAdminTagCategoryList({
    organizationId: qsOrganizationId,
    input: {
      limit: 100000,
      offset: 0,
    },
  });

  const options = useMemo(
    () =>
      tagCategories.filter(
        (tagCategory) =>
          !(excludeTagCategoryIds ?? []).includes(tagCategory.id),
      ),
    [excludeTagCategoryIds, tagCategories],
  );

  const formatOptionLabel = useCallback(
    (tagCategory: InternalAdminTagCategoryFragment) => (
      <Text>{tagCategory.name}</Text>
    ),
    [],
  );

  const getOptionLabel = useCallback(
    (tagCategory: InternalAdminTagCategoryFragment) => `${tagCategory.name}`,
    [],
  );

  const getOptionValue = useCallback(
    (tagCategory: InternalAdminTagCategoryFragment) => tagCategory.id,
    [],
  );

  const isEmpty = options.length === 0;

  return (
    <Select
      options={options}
      formatOptionLabel={formatOptionLabel}
      getOptionLabel={getOptionLabel}
      getOptionValue={getOptionValue}
      menuPlacement="bottom"
      onChange={onChange}
      components={{
        DropdownIndicator: () => null,
        IndicatorSeparator: () => null,
      }}
      chakraStyles={{
        placeholder: (provided: SystemStyleObject): SystemStyleObject => ({
          ...provided,
          fontStyle: "italic",
          fontSize: 12,
          color: colors.placeholderText,
          p: 0,
          m: 0,
        }),
      }}
      placeholder={
        isEmpty ? "No tag categories available" : "Select a tag category..."
      }
    />
  );
};

export default memo(InternalAdminTagCategorySelect);
